import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  Account,
  ExternalProvider,
  OAuthIssuer,
  OAuthStartRequest,
  ProviderAuthMethod,
} from '@/api';
import { useApi } from '@/api/provider';

/**
 * Signing a mailbox in with Google or Microsoft (ADR 0109).
 *
 * The server owns the whole sign-in: it answers `POST /accounts/oauth/start`
 * with the issuer's page, the browser goes there as a full-page navigation
 * (an installed PWA on iOS cannot open popups reliably), and the issuer sends
 * it back to the server's callback, which redirects to
 * `/settings/accounts?oauth=…`. Nothing here sees a token or an address the
 * user typed.
 */

/** The issuer's name as the user knows it. */
export const ISSUER_NAMES: Readonly<Record<OAuthIssuer, string>> = {
  google: 'Google',
  microsoft: 'Microsoft',
};

/**
 * The issuer a catalogue id signs in with, for when the catalogue is not to
 * hand (an older or unreachable `/accounts/providers`, a popped-out window).
 * Mirrors the backend's compiled-in table; the catalogue's `oauth_issuer`
 * wins whenever it is there.
 */
const KNOWN_ISSUERS: Readonly<Record<string, OAuthIssuer>> = { gmail: 'google', outlook: 'microsoft' };

/**
 * The methods a provider can be connected with here, in the server's order.
 * A backend that predates ADR 0109 sends no `auth_methods`: it only ever had
 * app passwords, so an available entry is that and nothing else.
 */
export function authMethodsOf(provider: ExternalProvider): readonly ProviderAuthMethod[] {
  if (provider.authMethods !== undefined) return provider.authMethods;
  return provider.available ? ['app_password'] : [];
}

/** The issuer to sign in with, when this provider offers sign-in here. */
export function oauthIssuerOf(provider: ExternalProvider): OAuthIssuer | undefined {
  if (!authMethodsOf(provider).includes('oauth2')) return undefined;
  return provider.oauthIssuer;
}

export function offersAppPassword(provider: ExternalProvider): boolean {
  return authMethodsOf(provider).includes('app_password');
}

/** True for a mailbox that signs in with Google or Microsoft rather than a password. */
export function signsInWithOAuth(account: Account): boolean {
  return account.auth === 'oauth2';
}

/** Whose sign-in an `oauth2` mailbox uses; `undefined` for a password mailbox. */
export function accountIssuer(
  account: Account,
  providers: readonly ExternalProvider[] = [],
): OAuthIssuer | undefined {
  if (!signsInWithOAuth(account) || account.provider === undefined) return undefined;
  return (
    providers.find((entry) => entry.id === account.provider)?.oauthIssuer ?? KNOWN_ISSUERS[account.provider]
  );
}

/** "Google", "Microsoft", or a neutral word when the issuer cannot be told. */
export function issuerName(issuer: OAuthIssuer | undefined, fallback = 'your provider'): string {
  return issuer === undefined ? fallback : ISSUER_NAMES[issuer];
}

/**
 * What the reconnect control says: "Sign in with Google again". The
 * provider's display name stands in when the issuer is unknown.
 */
export function reconnectLabel(account: Account, providers: readonly ExternalProvider[] = []): string {
  const issuer = accountIssuer(account, providers);
  return `Sign in with ${issuer ? ISSUER_NAMES[issuer] : (account.providerName ?? 'your provider')} again`;
}

/**
 * What a flagged mailbox needs, in the words of a sentence that starts with
 * its name: "needs its password again", or for a Google or Microsoft
 * mailbox, which has no password here, "needs you to sign in again".
 */
export function needsAgainText(account: Account): string {
  return signsInWithOAuth(account) ? 'needs you to sign in again' : 'needs its password again';
}

/* ------------------------------------------------------- the round trip */

/**
 * What this tab started, kept across the full-page round trip so the return
 * can say "Google" and name the mailbox a reconnect was for. The callback URL
 * carries only `oauth`, `account` and `reason` (ADR 0109); this is ours.
 * Session storage: per tab, gone with it, and never anything secret.
 */
export interface PendingOAuth {
  provider: string;
  issuer?: OAuthIssuer;
  accountId?: string;
}

const PENDING_KEY = 'ols.oauth.pending';

export function rememberPendingOAuth(pending: PendingOAuth): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Private mode or storage off: the return still works, in neutral words.
  }
}

/** Reads and forgets what this tab started; `undefined` when nothing (or nothing readable) was. */
export function takePendingOAuth(): PendingOAuth | undefined {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    if (raw === null) return undefined;
    const value = JSON.parse(raw) as Partial<PendingOAuth> | null;
    if (value === null || typeof value.provider !== 'string') return undefined;
    return {
      provider: value.provider,
      ...(value.issuer === 'google' || value.issuer === 'microsoft' ? { issuer: value.issuer } : {}),
      ...(typeof value.accountId === 'string' ? { accountId: value.accountId } : {}),
    };
  } catch {
    return undefined;
  }
}

/**
 * The one place the page is left for the issuer. An object so a test can
 * replace `assign` (jsdom does not navigate).
 */
export const oauthNavigation = {
  assign(url: string): void {
    window.location.assign(url);
  },
};

/** A path on this origin ("/settings/accounts?…"), which only the mock answers with. */
function isAppPath(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//');
}

/**
 * The only two places the page is ever left for: the issuers' sign-in pages
 * (the backend's compiled-in table, ADR 0109). Origins, so a path, query or
 * fragment never matters and a look-alike host never passes.
 */
const ISSUER_ORIGINS: ReadonlySet<string> = new Set([
  'https://accounts.google.com',
  'https://login.microsoftonline.com',
]);

/**
 * True when `url` is an https URL on one of the issuers' origins. The server
 * builds the authorize URL from its own table, so anything else is a bug or
 * a tampered answer, and following it would send the person -- mid-sign-in,
 * expecting a Google or Microsoft page -- somewhere else entirely.
 */
export function isIssuerAuthorizeUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return (
    parsed.protocol === 'https:' &&
    parsed.username === '' &&
    parsed.password === '' &&
    ISSUER_ORIGINS.has(parsed.origin)
  );
}

/** What a refused authorize URL is shown as: the same words as any sign-in that could not start. */
export const OAUTH_START_FAILED = 'Signing in could not start. Try again.';

export interface StartOAuth {
  /** Asks the server for the sign-in page and goes there. Rejects with the server's refusal. */
  start: (input: OAuthStartRequest, issuer: OAuthIssuer | undefined) => Promise<void>;
  /**
   * True from the server's answer until the page is gone. A form's
   * unsaved-changes guard must be off by then, or the browser would ask
   * "Leave site?" about the very navigation the user asked for.
   */
  leaving: boolean;
}

export function useStartOAuth(): StartOAuth {
  const api = useApi();
  const navigate = useNavigate();
  const [target, setTarget] = useState<string | null>(null);

  /*
   * Followed in an effect, not in `start`: the render that sets `target` is
   * the one in which the caller's guard turns off, and React runs every
   * effect cleanup of a commit (the guard's `beforeunload` removal) before
   * any new effect (this one).
   */
  useEffect(() => {
    // `start` already refused anything else; checked again at the one line
    // that leaves the page, so no other way of setting `target` can skip it.
    if (target !== null && isIssuerAuthorizeUrl(target)) oauthNavigation.assign(target);
  }, [target]);

  // Back from the issuer's page can restore this page from the back-forward
  // cache exactly as it was left, mid-"Taking you to Google…". Start over.
  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) setTarget(null);
    };
    window.addEventListener('pageshow', restored);
    return () => window.removeEventListener('pageshow', restored);
  }, []);

  const start = useCallback(
    async (input: OAuthStartRequest, issuer: OAuthIssuer | undefined) => {
      const { authorizeUrl } = await api.accounts.oauthStart(input);
      // Checked before anything is remembered or followed. The mock's
      // in-app path is the one exception, and stays in this origin.
      if (!isAppPath(authorizeUrl) && !isIssuerAuthorizeUrl(authorizeUrl)) {
        throw new Error(OAUTH_START_FAILED);
      }
      rememberPendingOAuth({
        provider: input.provider,
        ...(issuer ? { issuer } : {}),
        ...(input.accountId !== undefined ? { accountId: input.accountId } : {}),
      });
      // The mock's "callback" is this same page: followed in-app, without a
      // reload that would empty its in-memory data. Neither the router's
      // blocker (a search change) nor `beforeunload` is involved.
      if (isAppPath(authorizeUrl)) navigate(authorizeUrl, { replace: true });
      else setTarget(authorizeUrl);
    },
    [api, navigate],
  );

  return { start, leaving: target !== null };
}
